/**
 * Local registry of Aave receipt tokens (aTokens) that the LI.FI catalog
 * does not list. Consumed by `contractPresets.ts` to populate `fromToken`
 * when an Aave preset is activated; NOT merged into the dynamic select
 * (chains + stables come from the GraphQL API at runtime).
 *
 * Addresses are checksummed (EIP-55).
 */

export interface TokenDef {
  readonly symbol: string;
  readonly name: string;
  readonly address: `0x${string}`;
  readonly decimals: number;
  readonly isNative?: boolean;
  /** Optional LI.FI-provided logo URL. Selects render a fallback symbol if absent or load fails. */
  readonly logoURI?: string;
  /**
   * Indicative USD price (passthrough from LI.FI, ~minutes lag). Use only for
   * UI display — for live execution price read `estimate.{from,to}AmountUSD`
   * from the buildTx response.
   */
  readonly priceUSD?: string;
}

export interface ChainDef {
  readonly id: number;
  readonly name: string;
  readonly shortName: string;
  /** Used to build explorer links + native-token symbol fallback. */
  readonly nativeSymbol: string;
  readonly tokens: readonly TokenDef[];
  /** Optional chain logo URL from the catalog. */
  readonly logoURI?: string;
  /**
   * Whether this chain is a mainnet (vs testnet). Comes from LI.FI's
   * `chain.mainnet` field. Used to surface a "Testnet" badge in the UI.
   */
  readonly mainnet?: boolean;
  /**
   * Whether swapgate has an Executor contract deployed on this chain,
   * derived from `EXECUTORS_JSON` in the worker config. When true, the
   * BEFORE position flow (contract → swap → call) is available.
   */
  readonly allowBefore?: boolean;
}

export const NATIVE_ADDRESS =
  "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE" as const;

/**
 * Aave Polygon receipt tokens. NOT mergeable into the dynamic catalog —
 * consumed by contractPresets.ts to populate fromToken when the user
 * activates an Aave preset (then the address auto-fills bypassing the select).
 */
export const LOCAL_ATOKENS_BY_CHAIN: Readonly<
  Record<number, readonly TokenDef[]>
> = {
  137: [
    {
      symbol: "aPolUSDT",
      name: "Aave Polygon USDT",
      address: "0x6ab707Aca953eDAeFBc4fD23bA73294241490620",
      decimals: 6,
    },
    {
      symbol: "aPolUSDCn",
      name: "Aave Polygon USDC (native)",
      address: "0xA4D94019934D8333Ef880ABFFbF2FDd611C762BD",
      decimals: 6,
    },
    {
      symbol: "aPolUSDC",
      name: "Aave Polygon USDC.e (legacy)",
      address: "0x625E7708f30cA75bfd92586e17077590C60eb4cD",
      decimals: 6,
    },
  ],
} as const;

/**
 * Resolve a chain by id from the local aToken registry. After the dynamic
 * catalog merge (waves 3.6/3.7) this helper will accept the merged catalog;
 * for now it only sees chains that have local aTokens registered.
 */
export function findChain(id: number): ChainDef | undefined {
  const tokens = LOCAL_ATOKENS_BY_CHAIN[id];
  if (tokens === undefined) return undefined;
  // Minimal ChainDef synthesised from the local registry. Display fields
  // (name/shortName/nativeSymbol) will be replaced by the dynamic catalog
  // once the merge lands; consumers that only need `tokens` keep working.
  return {
    id,
    name: `Chain ${id}`,
    shortName: `Chain ${id}`,
    nativeSymbol: "",
    tokens,
  };
}

/**
 * Lookup token metadata by address within a chain. Case-insensitive on
 * address — viem's checksummed list, browser-pasted lowercase, and
 * legacy mixed-case all resolve to the same entry.
 */
export function findToken(
  chainId: number,
  address: string,
): TokenDef | undefined {
  const tokens = LOCAL_ATOKENS_BY_CHAIN[chainId];
  if (tokens === undefined) return undefined;
  const lowered = address.toLowerCase();
  return tokens.find((t) => t.address.toLowerCase() === lowered);
}
